import React, { FC, useState } from 'react'

import { EuiAccordion, EuiButton, EuiButtonIcon, EuiFlexGroup, EuiFlexItem, EuiSpacer, EuiText, EuiToolTip, useGeneratedHtmlId } from '@elastic/eui'

import { CompetitorProfile, GeographicCluster, MarketAnalysis, MarketChallenge } from 'api/interfaces/MarketAnalysis'
import { AIPlanCustomSection } from 'api/interfaces/AIPlan'
import { AIPlanMarketAnalysisSection, MarketAnalysisCustomSectionPosition } from 'api/interfaces/AIPlanMarketAnalysisSection'
import AIPlannerMarkdownRenderer from 'features/planner/components/AIPlannerMarkdownRenderer'
import AgencyV2Modal from 'features/planner/components/AgencyV2Modal'
import SparklesIcon from 'features/planner/components/SparklesIcon'
import { AddCustomSectionButton } from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/AddCustomSectionButton'

import EditCompetitorProfileFlyout from './EditCompetitorProfileFlyout'
import EditGeographicClusterFlyout from './EditGeographicClusterFlyout'
import EditMarketChallengeFlyout from './EditMarketChallengeFlyout'
import MarketAnalysisSection from './MarketAnalysisSection'

interface StructuredMarketAnalysisProps {
  marketAnalysis: MarketAnalysis
  onSectionEdit: (sectionName: string, fieldKey: string, value: string) => void
  onAddCompetitorProfile: (profile: CompetitorProfile) => void
  onEditCompetitorProfile: (index: number, profile: CompetitorProfile) => void
  onRemoveCompetitorProfile: (index: number) => void
  onAddGeographicCluster: (cluster: GeographicCluster) => void
  onEditGeographicCluster: (index: number, cluster: GeographicCluster) => void
  onRemoveGeographicCluster: (index: number) => void
  onAddMarketChallenge: (challenge: MarketChallenge) => void
  onEditMarketChallenge: (index: number, challenge: MarketChallenge) => void
  onRemoveMarketChallenge: (index: number) => void
  editMode: boolean
  onAIEditClick?: (sectionName: string, fieldKey: string, fieldLabel: string, currentValue: string) => void
  onAISectionEditClick?: (sectionName: string, sectionLabel: string) => void
  onEditTitleClick?: (sectionName: string, fieldKey: string, currentLabel: string) => void
  onEditSectionTitleClick?: (sectionName: string, currentTitle: string) => void
  aiMarketAnalysis?: MarketAnalysis
  onResetToOriginal?: () => void
  isUpdating?: boolean
  showBlueprintWarning?: boolean
  marketAnalysisSection?: AIPlanMarketAnalysisSection | null
  onAddCustomSection?: (position: MarketAnalysisCustomSectionPosition) => void
  onEditCustomSection?: (position: MarketAnalysisCustomSectionPosition, index: number) => void
  onDeleteCustomSection?: (position: MarketAnalysisCustomSectionPosition, index: number) => void
}

type CompetitorProfileFlyoutState = { open: false } | { open: true; mode: 'add' | 'edit'; index?: number; profile?: CompetitorProfile }
type GeographicClusterFlyoutState = { open: false } | { open: true; mode: 'add' | 'edit'; index?: number; cluster?: GeographicCluster }
type MarketChallengeFlyoutState = { open: false } | { open: true; mode: 'add' | 'edit'; index?: number; challenge?: MarketChallenge }

const competitorProfilesToMarkdown = (profiles: CompetitorProfile[]): string => {
  return profiles.map(profile => {
    const parts: string[] = []
    parts.push(`### ${profile.name}`)
    if (profile.strategicThreatLevel) parts.push(`**Threat Level:** ${profile.strategicThreatLevel}`)
    if (profile.overview) parts.push(`${profile.overview}`)
    if (profile.strengths) parts.push(`**Strengths:** ${profile.strengths}`)
    if (profile.weaknesses) parts.push(`**Weaknesses:** ${profile.weaknesses}`)
    if (profile.strategicImplications) parts.push(`**Strategic Implications:** ${profile.strategicImplications}`)
    return parts.join('\n\n')
  }).join('\n\n---\n\n')
}

const geographicClustersToMarkdown = (clusters: GeographicCluster[]): string => {
  return clusters.map(cluster => {
    const parts: string[] = []
    parts.push(`### ${cluster.clusterName}`)
    if (cluster.priorityLevel) parts.push(`**Priority Level:** ${cluster.priorityLevel}`)
    if (cluster.alignmentScore) parts.push(`**Alignment Score:** ${cluster.alignmentScore}`)
    if (cluster.keyAreas) parts.push(`**Key Areas:** ${cluster.keyAreas}`)
    if (cluster.strategicRationale) parts.push(`**Strategic Rationale:** ${cluster.strategicRationale}`)
    return parts.join('\n\n')
  }).join('\n\n---\n\n')
}

const marketChallengesToMarkdown = (challenges: MarketChallenge[]): string => {
  return challenges.map(challenge => {
    const parts: string[] = []
    parts.push(`### ${challenge.title}`)
    if (challenge.severity) parts.push(`**Severity:** ${challenge.severity}`)
    if (challenge.description) parts.push(`${challenge.description}`)
    if (challenge.operationalImplications) parts.push(`**Operational Implications:** ${challenge.operationalImplications}`)
    return parts.join('\n\n')
  }).join('\n\n---\n\n')
}

interface ArrayItemRowProps {
  label: string
  index: number
  onEdit: (index: number) => void
  onRemove: (index: number) => void
  removeModalTitle: string
  removeModalDescription: string
}

const ArrayItemRow: FC<ArrayItemRowProps> = ({ label, index, onEdit, onRemove, removeModalTitle, removeModalDescription }) => {
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)

  return (
    <>
      <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
        <EuiFlexItem>
          <EuiText size="s"><strong>{label}</strong></EuiText>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButtonIcon iconType="pencil" style={{ color: '#0071c2' }} onClick={() => onEdit(index)} aria-label={`Edit ${label}`} size="s" />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButtonIcon iconType="trash" style={{ color: '#BD271E' }} onClick={() => setIsDeleteModalVisible(true)} aria-label={`Remove ${label}`} size="s" />
        </EuiFlexItem>
      </EuiFlexGroup>
      {isDeleteModalVisible && (
        <AgencyV2Modal isLoading={false} title={removeModalTitle} onClose={() => setIsDeleteModalVisible(false)} confirmButtonText="Remove" onConfirmButtonClick={() => { onRemove(index); setIsDeleteModalVisible(false) }} showCancelButton>
          <EuiText>{removeModalDescription}</EuiText>
        </AgencyV2Modal>
      )}
    </>
  )
}

const StructuredMarketAnalysis: FC<StructuredMarketAnalysisProps> = ({
  marketAnalysis,
  onSectionEdit,
  onAddCompetitorProfile,
  onEditCompetitorProfile,
  onRemoveCompetitorProfile,
  onAddGeographicCluster,
  onEditGeographicCluster,
  onRemoveGeographicCluster,
  onAddMarketChallenge,
  onEditMarketChallenge,
  onRemoveMarketChallenge,
  editMode,
  onAIEditClick,
  onAISectionEditClick,
  onEditTitleClick,
  onEditSectionTitleClick,
  onResetToOriginal,
  isUpdating = false,
  showBlueprintWarning = false,
  marketAnalysisSection,
  onAddCustomSection,
  onEditCustomSection,
  onDeleteCustomSection
}) => {
  const accordionIdPrefix = useGeneratedHtmlId({ prefix: 'marketAnalysis' })

  const [competitorProfileFlyout, setCompetitorProfileFlyout] = useState<CompetitorProfileFlyoutState>({ open: false })
  const [geographicClusterFlyout, setGeographicClusterFlyout] = useState<GeographicClusterFlyoutState>({ open: false })
  const [marketChallengeFlyout, setMarketChallengeFlyout] = useState<MarketChallengeFlyoutState>({ open: false })

  const strategicOverviewFields = [
    { key: 'businessPositioning', label: 'Business Positioning', value: marketAnalysis.strategicOverview?.businessPositioning },
    { key: 'coreMarketChallengesAndOpportunities', label: 'Core Market Challenges & Opportunities', value: marketAnalysis.strategicOverview?.coreMarketChallengesAndOpportunities },
    { key: 'analyticalFramework', label: 'Analytical Framework', value: marketAnalysis.strategicOverview?.analyticalFramework }
  ]

  const industryContextFields = [
    { key: 'marketSizeAndGrowth', label: 'Market Size & Growth', value: marketAnalysis.industryContextAnalysis?.marketSizeAndGrowth },
    { key: 'transformativeTrends', label: 'Transformative Trends', value: marketAnalysis.industryContextAnalysis?.transformativeTrends },
    { key: 'regulatoryConsiderations', label: 'Regulatory Considerations', value: marketAnalysis.industryContextAnalysis?.regulatoryConsiderations },
    { key: 'seasonalImpacts', label: 'Seasonal Impacts', value: marketAnalysis.industryContextAnalysis?.seasonalImpacts },
    { key: 'relatedIndustryInfluences', label: 'Related Industry Influences', value: marketAnalysis.industryContextAnalysis?.relatedIndustryInfluences },
    { key: 'economicIndicators', label: 'Economic Indicators', value: marketAnalysis.industryContextAnalysis?.economicIndicators }
  ]

  const competitiveLandscapeTextFields = [
    { key: 'competitiveAssessmentMethodology', label: 'Competitive Assessment Methodology', value: marketAnalysis.competitiveLandscape?.competitiveAssessmentMethodology },
    { key: 'marketPositioningAnalysis', label: 'Market Positioning Analysis', value: marketAnalysis.competitiveLandscape?.marketPositioningAnalysis },
    { key: 'shareOfVoiceAnalysis', label: 'Share of Voice Analysis', value: marketAnalysis.competitiveLandscape?.shareOfVoiceAnalysis },
    { key: 'gapAnalysis', label: 'Gap Analysis', value: marketAnalysis.competitiveLandscape?.gapAnalysis },
    { key: 'competitivePositioningOpportunities', label: 'Competitive Positioning Opportunities', value: marketAnalysis.competitiveLandscape?.competitivePositioningOpportunities }
  ]

  const geographicOpportunitiesTextFields = [
    { key: 'geographicTargetingMethodology', label: 'Geographic Targeting Methodology', value: marketAnalysis.geographicOpportunities?.geographicTargetingMethodology },
    { key: 'regionalDemandAnalysis', label: 'Regional Demand Analysis', value: marketAnalysis.geographicOpportunities?.regionalDemandAnalysis },
    { key: 'competitiveDensityAnalysis', label: 'Competitive Density Analysis', value: marketAnalysis.geographicOpportunities?.competitiveDensityAnalysis },
    { key: 'demographicAnalysis', label: 'Demographic Analysis', value: marketAnalysis.geographicOpportunities?.demographicAnalysis },
    { key: 'environmentalConsiderations', label: 'Environmental Considerations', value: marketAnalysis.geographicOpportunities?.environmentalConsiderations }
  ]

  const seasonalPatternsFields = [
    { key: 'keySeasonalCycles', label: 'Key Seasonal Cycles', value: marketAnalysis.seasonalAndCyclicalPatterns?.keySeasonalCycles },
    { key: 'externalFactorAnalysis', label: 'External Factor Analysis', value: marketAnalysis.seasonalAndCyclicalPatterns?.externalFactorAnalysis },
    { key: 'demandVariationQuantification', label: 'Demand Variation Quantification', value: marketAnalysis.seasonalAndCyclicalPatterns?.demandVariationQuantification },
    { key: 'consumerBehaviorTrends', label: 'Consumer Behavior Trends', value: marketAnalysis.seasonalAndCyclicalPatterns?.consumerBehaviorTrends }
  ]

  const coreChallengesTextFields = [
    { key: 'challengePrioritizationAnalysis', label: 'Challenge Prioritization Analysis', value: marketAnalysis.coreChallengesInMarket?.challengePrioritizationAnalysis },
    { key: 'marketConstraintAnalysis', label: 'Market Constraint Analysis', value: marketAnalysis.coreChallengesInMarket?.marketConstraintAnalysis }
  ]

  const insightsSummaryFields = [
    { key: 'keyFindingsSynthesis', label: 'Key Findings Synthesis', value: marketAnalysis.insightsSummary?.keyFindingsSynthesis },
    { key: 'strategicDirection', label: 'Strategic Direction', value: marketAnalysis.insightsSummary?.strategicDirection },
    { key: 'businessSpecificStrategicInsights', label: 'Business-Specific Strategic Insights', value: marketAnalysis.insightsSummary?.businessSpecificStrategicInsights }
  ]

  const competitorProfiles = marketAnalysis.competitiveLandscape?.keyCompetitorProfiles ?? []
  const geographicClusters = marketAnalysis.geographicOpportunities?.opportunityScoringSystem ?? []
  const marketChallenges = marketAnalysis.coreChallengesInMarket?.coreChallengesInTheMarket ?? []

  const positions: MarketAnalysisCustomSectionPosition[] = [
    'aboveStrategicOverview', 'aboveIndustryContext', 'aboveCompetitiveLandscape',
    'aboveGeographicOpportunities', 'aboveSeasonalPatterns', 'aboveChallenges',
    'aboveInsightsSummary', 'aboveBottom'
  ]
  const standardSectionKeys = [
    'strategicOverview', 'industryContextAnalysis', 'competitiveLandscape',
    'geographicOpportunities', 'seasonalAndCyclicalPatterns', 'coreChallengesInMarket',
    'insightsSummary'
  ]

  const sectionNumbers: Record<string, number> = {}
  const customSectionStartNumbers: Record<string, number> = {}
  let counter = 1
  for (let i = 0; i < positions.length; i++) {
    const customCount = (marketAnalysisSection?.[positions[i]] ?? []).length
    customSectionStartNumbers[positions[i]] = counter
    counter += customCount
    if (i < standardSectionKeys.length) {
      sectionNumbers[standardSectionKeys[i]] = counter
      counter += 1
    }
  }

  const sectionTitle = (sectionName: string, titleKey: string, defaultTitle: string) => {
    const title = (marketAnalysisSection as any)?.[titleKey] ?? defaultTitle
    const number = sectionNumbers[sectionName]
    return (
      <span style={{ fontSize: '2rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
        {number}. {title}
        {editMode && (
          <>
            <EuiToolTip content="Edit Title">
              <EuiButtonIcon iconType="visText" style={{ color: '#0071c2' }} onClick={(e: React.MouseEvent) => { e.stopPropagation(); onEditSectionTitleClick?.(sectionName, title) }} aria-label={`Edit Title ${title}`} size="s" />
            </EuiToolTip>
            <EuiToolTip content="AI Edit">
              <EuiButtonIcon iconType={SparklesIcon} onClick={(e: React.MouseEvent) => { e.stopPropagation(); onAISectionEditClick?.(sectionName, title) }} aria-label={`AI Edit ${title}`} size="s" />
            </EuiToolTip>
          </>
        )}
      </span>
    )
  }

  const customSectionTitle = (number: number, title: string, position: MarketAnalysisCustomSectionPosition, index: number) => (
    <span style={{ fontSize: '2rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
      {number}. {title || 'Untitled Section'}
      {editMode && (
        <>
          <EuiToolTip content="Edit">
            <EuiButtonIcon iconType="pencil" style={{ color: '#0071c2' }} onClick={(e: React.MouseEvent) => { e.stopPropagation(); onEditCustomSection?.(position, index) }} aria-label={`Edit ${title}`} size="s" />
          </EuiToolTip>
          <EuiToolTip content="Delete">
            <EuiButtonIcon iconType="trash" style={{ color: '#BD271E' }} onClick={(e: React.MouseEvent) => { e.stopPropagation(); onDeleteCustomSection?.(position, index) }} aria-label={`Delete ${title}`} size="s" />
          </EuiToolTip>
        </>
      )}
    </span>
  )

  const renderCustomSectionsAtPosition = (position: MarketAnalysisCustomSectionPosition) => {
    const sections: AIPlanCustomSection[] = marketAnalysisSection?.[position] as AIPlanCustomSection[] ?? []
    const startNumber = customSectionStartNumbers[position]

    return (
      <>
        {sections.filter(s => !s.hideAll).map((section, index, filtered) => (
          <React.Fragment key={section.id || index}>
            <EuiAccordion
              id={`${accordionIdPrefix}_custom_${position}_${index}`}
              buttonContent={customSectionTitle(startNumber + index, section.title, position, index)}
              initialIsOpen
              paddingSize="l"
            >
              {section.content && <AIPlannerMarkdownRenderer content={section.content} />}
            </EuiAccordion>
            {index < filtered.length - 1 && <EuiSpacer size="m" />}
          </React.Fragment>
        ))}
        {editMode && onAddCustomSection ? (
          <>
            <div className="ma-gap-before-add"><EuiSpacer size="m" /></div>
            <AddCustomSectionButton position={position} onClick={() => onAddCustomSection(position)} />
            <EuiSpacer size="m" />
          </>
        ) : (
          <EuiSpacer size="m" />
        )}
      </>
    )
  }

  return (
    <>
      <style>{`.euiAccordion-isOpen + .ma-gap-before-add { display: none; }`}</style>
      {onResetToOriginal && editMode && (
        <>
          <EuiFlexGroup justifyContent="flexEnd">
            <EuiFlexItem grow={false}>
              <EuiButton size="s" color="warning" onClick={onResetToOriginal} isLoading={isUpdating}>
                Reset to AI Original
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiSpacer size="m" />
        </>
      )}

      {renderCustomSectionsAtPosition('aboveStrategicOverview')}

      <EuiAccordion id={`${accordionIdPrefix}_strategicOverview`} buttonContent={sectionTitle('strategicOverview', 'strategicOverviewTitle', 'Strategic Overview')} initialIsOpen paddingSize="l">
        <MarketAnalysisSection
          sectionName="strategicOverview"
          fields={strategicOverviewFields}
          onFieldEdit={onSectionEdit}
          editMode={editMode}
          onAIEditClick={onAIEditClick}
          onEditTitleClick={onEditTitleClick}
          isUpdating={isUpdating}
          showBlueprintWarning={showBlueprintWarning}
        />
      </EuiAccordion>

      {renderCustomSectionsAtPosition('aboveIndustryContext')}

      <EuiAccordion id={`${accordionIdPrefix}_industryContextAnalysis`} buttonContent={sectionTitle('industryContextAnalysis', 'industryContextAnalysisTitle', 'Industry Context Analysis')} initialIsOpen paddingSize="l">
        <MarketAnalysisSection
          sectionName="industryContextAnalysis"
          fields={industryContextFields}
          onFieldEdit={onSectionEdit}
          editMode={editMode}
          onAIEditClick={onAIEditClick}
          onEditTitleClick={onEditTitleClick}
          isUpdating={isUpdating}
          showBlueprintWarning={showBlueprintWarning}
        />
      </EuiAccordion>

      {renderCustomSectionsAtPosition('aboveCompetitiveLandscape')}

      <EuiAccordion id={`${accordionIdPrefix}_competitiveLandscape`} buttonContent={sectionTitle('competitiveLandscape', 'competitiveLandscapeTitle', 'Competitive Landscape')} initialIsOpen paddingSize="l">
        <MarketAnalysisSection
          sectionName="competitiveLandscape"
          fields={competitiveLandscapeTextFields}
          onFieldEdit={onSectionEdit}
          editMode={editMode}
          onAIEditClick={onAIEditClick}
          onEditTitleClick={onEditTitleClick}
          isUpdating={isUpdating}
        />
        {competitorProfiles.length > 0 && (
          <>
            <EuiSpacer size="l" />
            {editMode ? (
              <>
                <EuiFlexGroup justifyContent="flexStart">
                  <EuiFlexItem grow={false}>
                    <EuiButton size="s" iconType="plus" color="primary" onClick={() => setCompetitorProfileFlyout({ open: true, mode: 'add' })}>
                      Add Competitor Profile
                    </EuiButton>
                  </EuiFlexItem>
                </EuiFlexGroup>
                <EuiSpacer size="m" />
                {competitorProfiles.map((profile, idx) => (
                  <React.Fragment key={idx}>
                    <ArrayItemRow
                      label={profile.name}
                      index={idx}
                      onEdit={i => setCompetitorProfileFlyout({ open: true, mode: 'edit', index: i, profile: competitorProfiles[i] })}
                      onRemove={onRemoveCompetitorProfile}
                      removeModalTitle="Remove Competitor Profile"
                      removeModalDescription={`Competitor profile: ${profile.name} will be removed.`}
                    />
                    {idx < competitorProfiles.length - 1 && <EuiSpacer size="s" />}
                  </React.Fragment>
                ))}
              </>
            ) : (
              <AIPlannerMarkdownRenderer content={competitorProfilesToMarkdown(competitorProfiles)} />
            )}
          </>
        )}
        {competitorProfiles.length === 0 && editMode && (
          <>
            <EuiSpacer size="l" />
            <EuiFlexGroup justifyContent="flexStart">
              <EuiFlexItem grow={false}>
                <EuiButton size="s" iconType="plus" color="primary" onClick={() => setCompetitorProfileFlyout({ open: true, mode: 'add' })}>
                  Add Competitor Profile
                </EuiButton>
              </EuiFlexItem>
            </EuiFlexGroup>
          </>
        )}
      </EuiAccordion>

      {renderCustomSectionsAtPosition('aboveGeographicOpportunities')}

      <EuiAccordion id={`${accordionIdPrefix}_geographicOpportunities`} buttonContent={sectionTitle('geographicOpportunities', 'geographicOpportunitiesTitle', 'Geographic Opportunities')} initialIsOpen paddingSize="l">
        <MarketAnalysisSection
          sectionName="geographicOpportunities"
          fields={geographicOpportunitiesTextFields}
          onFieldEdit={onSectionEdit}
          editMode={editMode}
          onAIEditClick={onAIEditClick}
          onEditTitleClick={onEditTitleClick}
          isUpdating={isUpdating}
        />
        {geographicClusters.length > 0 && (
          <>
            <EuiSpacer size="l" />
            {editMode ? (
              <>
                <EuiFlexGroup justifyContent="flexStart">
                  <EuiFlexItem grow={false}>
                    <EuiButton size="s" iconType="plus" color="primary" onClick={() => setGeographicClusterFlyout({ open: true, mode: 'add' })}>
                      Add Geographic Cluster
                    </EuiButton>
                  </EuiFlexItem>
                </EuiFlexGroup>
                <EuiSpacer size="m" />
                {geographicClusters.map((cluster, idx) => (
                  <React.Fragment key={idx}>
                    <ArrayItemRow
                      label={cluster.clusterName}
                      index={idx}
                      onEdit={i => setGeographicClusterFlyout({ open: true, mode: 'edit', index: i, cluster: geographicClusters[i] })}
                      onRemove={onRemoveGeographicCluster}
                      removeModalTitle="Remove Geographic Cluster"
                      removeModalDescription={`Geographic cluster: ${cluster.clusterName} will be removed.`}
                    />
                    {idx < geographicClusters.length - 1 && <EuiSpacer size="s" />}
                  </React.Fragment>
                ))}
              </>
            ) : (
              <AIPlannerMarkdownRenderer content={geographicClustersToMarkdown(geographicClusters)} />
            )}
          </>
        )}
        {geographicClusters.length === 0 && editMode && (
          <>
            <EuiSpacer size="l" />
            <EuiFlexGroup justifyContent="flexStart">
              <EuiFlexItem grow={false}>
                <EuiButton size="s" iconType="plus" color="primary" onClick={() => setGeographicClusterFlyout({ open: true, mode: 'add' })}>
                  Add Geographic Cluster
                </EuiButton>
              </EuiFlexItem>
            </EuiFlexGroup>
          </>
        )}
      </EuiAccordion>

      {renderCustomSectionsAtPosition('aboveSeasonalPatterns')}

      <EuiAccordion id={`${accordionIdPrefix}_seasonalAndCyclicalPatterns`} buttonContent={sectionTitle('seasonalAndCyclicalPatterns', 'seasonalPatternsTitle', 'Seasonal & Cyclical Patterns')} initialIsOpen paddingSize="l">
        <MarketAnalysisSection
          sectionName="seasonalAndCyclicalPatterns"
          fields={seasonalPatternsFields}
          onFieldEdit={onSectionEdit}
          editMode={editMode}
          onAIEditClick={onAIEditClick}
          onEditTitleClick={onEditTitleClick}
          isUpdating={isUpdating}
          showBlueprintWarning={showBlueprintWarning}
        />
      </EuiAccordion>

      {renderCustomSectionsAtPosition('aboveChallenges')}

      <EuiAccordion id={`${accordionIdPrefix}_coreChallengesInMarket`} buttonContent={sectionTitle('coreChallengesInMarket', 'coreChallengesTitle', 'Core Challenges in Market')} initialIsOpen paddingSize="l">
        <MarketAnalysisSection
          sectionName="coreChallengesInMarket"
          fields={coreChallengesTextFields}
          onFieldEdit={onSectionEdit}
          editMode={editMode}
          onAIEditClick={onAIEditClick}
          onEditTitleClick={onEditTitleClick}
          isUpdating={isUpdating}
        />
        {marketChallenges.length > 0 && (
          <>
            <EuiSpacer size="l" />
            {editMode ? (
              <>
                <EuiFlexGroup justifyContent="flexStart">
                  <EuiFlexItem grow={false}>
                    <EuiButton size="s" iconType="plus" color="primary" onClick={() => setMarketChallengeFlyout({ open: true, mode: 'add' })}>
                      Add Market Challenge
                    </EuiButton>
                  </EuiFlexItem>
                </EuiFlexGroup>
                <EuiSpacer size="m" />
                {marketChallenges.map((challenge, idx) => (
                  <React.Fragment key={idx}>
                    <ArrayItemRow
                      label={challenge.title}
                      index={idx}
                      onEdit={i => setMarketChallengeFlyout({ open: true, mode: 'edit', index: i, challenge: marketChallenges[i] })}
                      onRemove={onRemoveMarketChallenge}
                      removeModalTitle="Remove Market Challenge"
                      removeModalDescription={`Market challenge: ${challenge.title} will be removed.`}
                    />
                    {idx < marketChallenges.length - 1 && <EuiSpacer size="s" />}
                  </React.Fragment>
                ))}
              </>
            ) : (
              <AIPlannerMarkdownRenderer content={marketChallengesToMarkdown(marketChallenges)} />
            )}
          </>
        )}
        {marketChallenges.length === 0 && editMode && (
          <>
            <EuiSpacer size="l" />
            <EuiFlexGroup justifyContent="flexStart">
              <EuiFlexItem grow={false}>
                <EuiButton size="s" iconType="plus" color="primary" onClick={() => setMarketChallengeFlyout({ open: true, mode: 'add' })}>
                  Add Market Challenge
                </EuiButton>
              </EuiFlexItem>
            </EuiFlexGroup>
          </>
        )}
      </EuiAccordion>

      {renderCustomSectionsAtPosition('aboveInsightsSummary')}

      <EuiAccordion id={`${accordionIdPrefix}_insightsSummary`} buttonContent={sectionTitle('insightsSummary', 'insightsSummaryTitle', 'Insights Summary')} initialIsOpen paddingSize="l">
        <MarketAnalysisSection
          sectionName="insightsSummary"
          fields={insightsSummaryFields}
          onFieldEdit={onSectionEdit}
          editMode={editMode}
          onAIEditClick={onAIEditClick}
          onEditTitleClick={onEditTitleClick}
          isUpdating={isUpdating}
          showBlueprintWarning={showBlueprintWarning}
        />
      </EuiAccordion>

      {renderCustomSectionsAtPosition('aboveBottom')}

      {competitorProfileFlyout.open && (
        <EditCompetitorProfileFlyout
          mode={competitorProfileFlyout.mode}
          profile={competitorProfileFlyout.mode === 'edit' ? competitorProfileFlyout.profile : undefined}
          onSave={profile => {
            if (competitorProfileFlyout.mode === 'add') {
              onAddCompetitorProfile(profile)
            } else if (competitorProfileFlyout.index !== undefined) {
              onEditCompetitorProfile(competitorProfileFlyout.index, profile)
            }
            setCompetitorProfileFlyout({ open: false })
          }}
          onClose={() => setCompetitorProfileFlyout({ open: false })}
          isUpdating={isUpdating}
          showBlueprintWarning={showBlueprintWarning}
        />
      )}

      {geographicClusterFlyout.open && (
        <EditGeographicClusterFlyout
          mode={geographicClusterFlyout.mode}
          cluster={geographicClusterFlyout.mode === 'edit' ? geographicClusterFlyout.cluster : undefined}
          onSave={cluster => {
            if (geographicClusterFlyout.mode === 'add') {
              onAddGeographicCluster(cluster)
            } else if (geographicClusterFlyout.index !== undefined) {
              onEditGeographicCluster(geographicClusterFlyout.index, cluster)
            }
            setGeographicClusterFlyout({ open: false })
          }}
          onClose={() => setGeographicClusterFlyout({ open: false })}
          isUpdating={isUpdating}
          showBlueprintWarning={showBlueprintWarning}
        />
      )}

      {marketChallengeFlyout.open && (
        <EditMarketChallengeFlyout
          mode={marketChallengeFlyout.mode}
          challenge={marketChallengeFlyout.mode === 'edit' ? marketChallengeFlyout.challenge : undefined}
          onSave={challenge => {
            if (marketChallengeFlyout.mode === 'add') {
              onAddMarketChallenge(challenge)
            } else if (marketChallengeFlyout.index !== undefined) {
              onEditMarketChallenge(marketChallengeFlyout.index, challenge)
            }
            setMarketChallengeFlyout({ open: false })
          }}
          onClose={() => setMarketChallengeFlyout({ open: false })}
          isUpdating={isUpdating}
          showBlueprintWarning={showBlueprintWarning}
        />
      )}
    </>
  )
}

export default StructuredMarketAnalysis
